import { getState, setState, setSilent, showToast, CONFIG, totalUSDFor, filteredTransactions } from './store.js';
import { walletCall } from './lib/wallet.js';
import { haptic } from './lib/haptic.js';
import {
  generateVaultKey, createPinWrap, unwrapWithPin,
  platformAuthenticatorAvailable, registerWebauthn, unwrapWithWebauthn,
  encryptJSON, decryptJSON, setSessionVault, getSessionVault, clearSessionVault
} from './lib/vault.js';
import { buildStatementPdf } from './lib/pdf.js';

const { FX, CHAINS, NET } = CONFIG;
const CLIPBOARD_CLEAR_MS = 30000;
let clipboardTimer = null;

export function go(screen) {
  setState({ screen });
}

// 'system' clears the stored override so style.css's prefers-color-scheme
// media query decides; 'light'/'dark' pin it. Applying the data-theme
// attribute to <html> itself happens in app.js's render() (that's DOM the
// virtual-DOM tree never covers, since it's outside #app).
export function setTheme(theme) {
  try {
    if (theme === 'system') localStorage.removeItem('tynti_theme');
    else localStorage.setItem('tynti_theme', theme);
  } catch { /* storage unavailable */ }
  setState({ theme });
}

export function setOnline(online) {
  setState({ online });
}
export function setWorkerConnected(connected) {
  setState({ workerConnected: connected });
}
export function openStatusPanel() {
  setState({ showStatusPanel: true });
}
export function closeStatusPanel() {
  setState({ showStatusPanel: false });
}

export function openProfileMenu() {
  setState({ showProfileMenu: true });
}
export function closeProfileMenu() {
  setState({ showProfileMenu: false });
}
// Wallets/transactions/settings are global state, not scoped per profile
// (feature-ui.md only asked for the switcher itself, not per-profile
// wallet data — a separate, larger data-model decision left for later).
// Saved recipients ARE per-profile now (encrypted under each profile's own
// vault key — see vault.js) and that key only exists once a profile has
// been unlocked, so switching profiles can't just swap `activeProfileId`
// and keep going: the newly-selected profile's vault key isn't in memory,
// and re-deriving it needs that profile's own PIN/biometric. Clearing the
// session vault and routing back through Landing's unlock screen is the
// correct behavior here, not just a defensive fallback — it closes what
// was otherwise a real gap (instant access to a different identity's
// encrypted data with zero re-auth).
export function switchProfile(id) {
  const s = getState();
  if (!s.profiles.some((p) => p.id === id)) return;
  try { localStorage.setItem('tynti_active_profile', id); } catch { /* storage unavailable */ }
  clearSessionVault();
  setState((st) => ({
    activeProfileId: id,
    showProfileMenu: false,
    screen: 'landing',
    lockCode: '', lockError: '',
    savedRecipients: [],
    privateKeys: st.privateKeys.map((k) => ({ ...k, revealed: false, words: null }))
  }));
}
export function goAddProfile() {
  setState({ showProfileMenu: false, screen: 'landing', landingAddingProfile: true, landingFormOpen: false });
}
// Safety-net back-out for the "add profile" entry point specifically: unlike
// a brand-new device (which has nowhere else to go), this path is reached
// from an already-unlocked Home session, so getting stuck mid-form with no
// way back would be a real dead end.
export function cancelAddProfile() {
  setState({ landingAddingProfile: false, landingFormOpen: false, screen: 'home' });
}

const COPY_FLASH_MS = 1500;
let copyFlashTimer = null;

// flashKey identifies which specific copy button just fired, so its own
// checkmark/label swap can show without lighting up every copy button on
// screen — the toast alone isn't a reliable-enough affordance on its own.
export async function copyText(text, label, flashKey) {
  if (!navigator.clipboard || !navigator.clipboard.writeText) {
    showToast('Copy failed');
    return;
  }
  try {
    await navigator.clipboard.writeText(text);
    haptic();
    showToast(label + ' copied · clipboard clears in 30s');
    clearTimeout(clipboardTimer);
    clipboardTimer = setTimeout(() => {
      navigator.clipboard.writeText('').catch(() => {});
    }, CLIPBOARD_CLEAR_MS);
    if (flashKey) {
      setState({ copyFlash: flashKey });
      clearTimeout(copyFlashTimer);
      copyFlashTimer = setTimeout(() => setState({ copyFlash: null }), COPY_FLASH_MS);
    }
  } catch {
    showToast('Copy failed');
  }
}

// Same-origin fetch of the app's own static file — not a network/WDK call
// in the sense CLAUDE.md restricts to src/workers/, just reading an asset
// this build already ships (public/llms.txt), so it can go straight in
// actions.js like copyText's other callers.
export async function copyLlmsTxt() {
  try {
    const res = await fetch('/llms.txt');
    const text = await res.text();
    await copyText(text, 'llms.txt', 'llmstxt');
  } catch {
    showToast('Copy failed');
  }
}

/* ---- Open account ------------------------------------------------ */
export function openOpenAccount() {
  setState({ showOpenAccount: true, newAcctName: '', newAcctOwner: '', newAcctError: '' });
}
export function cancelOpenAccount() {
  setState({ showOpenAccount: false });
}
export function saveOpenAccount() {
  const name = getState().newAcctName.trim();
  const owner = getState().newAcctOwner.trim();
  if (!name) { setState({ newAcctError: 'Enter an account name' }); return; }
  const id = 'acct' + Date.now();
  const wallets = [...getState().wallets, { id, name, owner, balances: { USDC: 0 } }];
  setState({ wallets, showOpenAccount: false });
  showToast('Account opened');
}

// Each dimension (name/balance) toggles its own asc/desc direction when
// clicked again; clicking the other dimension's icon switches to it at that
// dimension's primary direction (A–Z, highest balance first).
export function setAccountSort(dimension) {
  setState((s) => {
    const cur = s.accountSort;
    if (dimension === 'name') {
      return { accountSort: cur === 'name-asc' ? 'name-desc' : 'name-asc' };
    }
    return { accountSort: cur === 'balance-desc' ? 'balance-asc' : 'balance-desc' };
  });
}

/* ---- Account management ------------------------------------------------ */
// Account Detail (close-account, per-currency receiving addresses) was
// removed per the Home-screen feature pass — its only entry point was the
// account card's onclick, which is now claimed by inline name/owner editing
// and per-balance-row Transfer navigation. Closing an account and viewing
// receiving addresses are intentionally unreachable for now, to be rebuilt
// on a future screen.
export function renameAccount(id, name) {
  const trimmed = name.trim();
  if (!trimmed) { showToast('Enter an account name'); return; }
  setState((s) => ({ wallets: s.wallets.map((w) => (w.id === id ? { ...w, name: trimmed } : w)) }));
  showToast('Account renamed');
}
export function renameAccountOwner(id, owner) {
  setState((s) => ({ wallets: s.wallets.map((w) => (w.id === id ? { ...w, owner: owner.trim() } : w)) }));
  showToast('Owner updated');
}

/* ---- Home: inline name/owner editing ----------------------------------- */
export function startHomeEdit(accountId, field) {
  const w = getState().wallets.find((w) => w.id === accountId);
  if (!w) return;
  setState({ homeEdit: { accountId, field, draft: field === 'name' ? w.name : w.owner } });
}
export function setHomeEditDraft(v) {
  setSilent((s) => ({ homeEdit: { ...s.homeEdit, draft: v } }));
}
export function confirmHomeEdit() {
  const { accountId, field, draft } = getState().homeEdit;
  if (field === 'name') renameAccount(accountId, draft);
  else if (field === 'owner') renameAccountOwner(accountId, draft);
  setState({ homeEdit: { accountId: null, field: null, draft: '' } });
}
export function cancelHomeEdit() {
  setState({ homeEdit: { accountId: null, field: null, draft: '' } });
}
export function toggleBalanceInfo() {
  setState((s) => ({ showBalanceInfo: !s.showBalanceInfo }));
}
export function toggleActivityInfo() {
  setState((s) => ({ showActivityInfo: !s.showActivityInfo }));
}

// Derives (once) the receiving address for every account/currency pair so
// Home's per-balance explorer-link icon has something to link to. Called
// once at boot (main.js), not from render — a handful of cheap mock-worker
// calls for this app's small, static demo wallet set, not per-navigation.
export async function ensureHomeAddresses() {
  const s = getState();
  const jobs = [];
  s.wallets.forEach((w) => {
    Object.keys(w.balances).forEach((ccy) => {
      const key = w.id + '|' + ccy;
      if (s.homeAddresses[key]) return;
      const chain = NET[ccy] || '';
      jobs.push(
        walletCall('deriveAddress', { accountId: w.id, ccy, chain })
          .then(({ address }) => ({ key, address }))
          .catch(() => null)
      );
    });
  });
  if (!jobs.length) return;
  const results = await Promise.all(jobs);
  const patch = {};
  results.forEach((r) => { if (r) patch[r.key] = r.address; });
  if (Object.keys(patch).length) setState((st) => ({ homeAddresses: { ...st.homeAddresses, ...patch } }));
}

/* ---- Home: navigation to Transfer / Activity --------------------------- */
// Presets the shared `move` state (see Send, below) for an internal
// account-to-account transfer — Transfer is a restricted view onto the same
// state/actions Send uses, not a separate flow, since an internal transfer
// is already exactly the "account" branch of onMoveConfirm.
export function goTransfer(accountId, ccy) {
  setState({
    screen: 'transfer',
    move: {
      fromAccountId: accountId, ccy, chain: NET[ccy] || '', toKey: '',
      amount: '', memo: '', step: 'form', errors: {}, resultTx: null, sending: false
    }
  });
}
// Dedicated Send entry point (used by the tabbar) rather than the generic
// go('move'), so the tabbar's own intent is explicit and separable from
// go('move')'s other callers.
export function goSend() {
  setState({ screen: 'move' });
}
export function goActivityFromHome(txId) {
  setState({ screen: 'txns', txnFilter: 'all', txnAccountFilter: 'all', txnSearch: '', expandedTxId: txId });
}
// Resets chain to the new currency's default network, matching Receive's
// ccy-select behavior — Send doesn't need this (no chain field there).
export function setTransferCcy(ccy) {
  setState((s) => ({ move: { ...s.move, ccy, chain: (CHAINS[ccy] || [])[0] || '', errors: { ...s.move.errors, ccy: null } } }));
}

/* ---- Recipient management ---------------------------------------------- */
// Encrypted at rest under the active profile's vault key, keyed by profile
// id so each profile's recipients are only ever readable under that
// profile's own key (see switchProfile's comment on why profile switches
// now force a re-unlock). A missing vault key here means no profile is
// currently unlocked — recipients UI is never reachable in that state, so
// this is a silent no-op, not a real failure.
async function persistRecipients(list) {
  const { profileId, vaultKey } = getSessionVault();
  if (!vaultKey) return;
  try {
    const envelope = await encryptJSON(vaultKey, list);
    localStorage.setItem('tynti_recipients_enc_' + profileId, JSON.stringify(envelope));
  } catch { /* storage unavailable */ }
}

// Called once right after a successful unlock/onboarding (unlock,
// unlockWithWebauthn, submitOpenAccount) — never from render, and never
// synchronously at module load, since decryption needs the vault key that
// only exists post-unlock. Fails closed (empty list) on any error —
// corrupt data or a stale/mismatched key must never surface as a crash or,
// worse, someone else's recipients.
async function loadRecipientsForSession() {
  const { profileId, vaultKey } = getSessionVault();
  if (!vaultKey) { setState({ savedRecipients: [] }); return; }
  try {
    const raw = localStorage.getItem('tynti_recipients_enc_' + profileId);
    if (!raw) { setState({ savedRecipients: [] }); return; }
    const list = await decryptJSON(vaultKey, JSON.parse(raw));
    setState({ savedRecipients: Array.isArray(list) ? list : [] });
  } catch {
    setState({ savedRecipients: [] });
  }
}

export function openEditRecipient(id) {
  const r = getState().savedRecipients.find((r) => r.id === id);
  if (!r) return;
  setState({ recipientEdit: { id: r.id, name: r.name, address: r.address }, showAddRecipient: true, newRecipError: '' });
}
export function setRecipientEditField(field, value) {
  setSilent((s) => ({ recipientEdit: { ...s.recipientEdit, [field]: value } }));
}

export function deleteRecipient(id) {
  const s = getState();
  const list = s.savedRecipients.filter((r) => r.id !== id);
  const move = s.move.toKey === 'recipient:' + id ? { ...s.move, toKey: '' } : s.move;
  setState({ savedRecipients: list, move });
  persistRecipients(list);
  showToast('Recipient removed');
}

/* ---- Receive ------------------------------------------------------- */
// ensureReceiveAddress is only ever triggered from these action functions
// (event-handler call sites) and goReceive below — never from inside a
// render function. Calling a setState-triggering action synchronously
// from within render() causes reentrant nested renders, since setState
// notifies subscribers (which re-render) before the outer render call
// has finished building its own output.
export function setReceiveAccount(accountId) {
  setState((s) => ({ receive: { ...s.receive, accountId, ccy: s.reportingCurrency } }));
  ensureReceiveAddress();
}
export function setReceiveCcy(ccy) {
  setState((s) => {
    const chain = ccy === s.reportingCurrency ? '' : (CHAINS[ccy] || [])[0] || '';
    return { receive: { ...s.receive, ccy, chain } };
  });
  ensureReceiveAddress();
}
export function setReceiveChain(chain) {
  setState((s) => ({ receive: { ...s.receive, chain } }));
  ensureReceiveAddress();
}
export function goReceive() {
  setState({ screen: 'receive' });
  ensureReceiveAddress();
}
export function toggleReceiveDetails() {
  setState((s) => ({ receive: { ...s.receive, showDetails: !s.receive.showDetails } }));
}
// Derives (or re-derives) the receive address via the worker and caches it
// on state.receive so the render layer stays synchronous.
// Tracks the cache key currently being fetched so a call that lands while
// one is already in flight for the same inputs is a no-op. Without this,
// calling this from a render path (or several action calls in quick
// succession) can pile up overlapping fetches that keep re-arming the
// loading state after it should have settled.
let receiveAddrInFlight = null;

export async function ensureReceiveAddress() {
  const s = getState();
  const r = s.receive;
  const isCrypto = r.ccy !== s.reportingCurrency;
  if (!isCrypto) {
    if (r.address) {
      setState((st) => ({ receive: { ...st.receive, address: '', addressLoading: false, addressError: null } }));
    }
    return;
  }
  const chain = r.chain || (CHAINS[r.ccy] || [])[0] || '';
  const cacheKey = r.accountId + '|' + r.ccy + '|' + chain;
  if (r._addrKey === cacheKey && r.address) return;
  if (receiveAddrInFlight === cacheKey) return;

  receiveAddrInFlight = cacheKey;
  setState((st) => ({ receive: { ...st.receive, addressLoading: true, addressError: null } }));
  let address;
  try {
    ({ address } = await walletCall('deriveAddress', { accountId: r.accountId, ccy: r.ccy, chain }));
  } catch {
    receiveAddrInFlight = null;
    // Guard against a stale response landing after the user changed inputs again.
    const cur = getState().receive;
    if (cur.accountId + '|' + cur.ccy + '|' + (cur.chain || chain) !== cacheKey) return;
    setState((st) => ({ receive: { ...st.receive, addressLoading: false, addressError: "Couldn't generate address — try again" } }));
    return;
  }
  receiveAddrInFlight = null;
  const cur = getState().receive;
  if (cur.accountId + '|' + cur.ccy + '|' + (cur.chain || chain) !== cacheKey) return;
  setState((st) => ({ receive: { ...st.receive, chain, address, _addrKey: cacheKey, addressLoading: false, addressError: null } }));
}

/* ---- Send / move ---------------------------------------------------- */
export function setMoveField(field, value) {
  setState((s) => ({ move: { ...s.move, [field]: value, errors: { ...s.move.errors, [field]: null } } }));
}
export function setFromAccount(accountId) {
  const w = getState().wallets.find((w) => w.id === accountId);
  const firstCcy = Object.keys(w.balances)[0] || getState().reportingCurrency;
  setState((s) => ({
    move: { ...s.move, fromAccountId: accountId, ccy: firstCcy, toKey: '', errors: {} }
  }));
}

// Clicking a balance row in Send's balance box (Home-style, one row per
// currency the From account holds) sets both the currency and amount to
// that row's full balance in one action — this replaces the removed "Max"
// button as the way to pick a whole-balance amount, but also works for
// any currency, not just the one already selected.
export function setSendBalance(ccy) {
  const s = getState();
  const account = s.wallets.find((w) => w.id === s.move.fromAccountId);
  const avail = ccy === s.reportingCurrency ? totalUSDFor(account) : (account.balances[ccy] || 0);
  setState((st) => ({
    move: { ...st.move, ccy, amount: String(+avail.toFixed(2)), errors: { ...st.move.errors, ccy: null, amount: null } }
  }));
}

export function validateMove() {
  const s = getState();
  const m = s.move;
  const errors = {};
  const account = s.wallets.find((w) => w.id === m.fromAccountId);
  const avail = m.ccy === s.reportingCurrency ? totalUSDFor(account) : (account.balances[m.ccy] || 0);
  const amt = parseFloat(m.amount);
  if (!m.amount || Number.isNaN(amt) || amt <= 0) errors.amount = 'Enter a valid amount';
  else if (amt > avail) errors.amount = 'Exceeds available balance';
  if (!m.toKey) errors.toKey = 'Choose a destination';
  return errors;
}

// Blur-time validation only checks *format* (amount parses, isn't over
// balance) — not required-ness, which stays a submit-time check so tabbing
// through an empty field doesn't show an error before the user has typed
// anything. setSilent, not setState: this field's blur is often followed
// immediately by clicking "Continue"/"Continue to review" — a setState
// here fully rebuilds the DOM mid-click (see app.js's render()), which can
// destroy and recreate that very button before the click completes,
// silently swallowing it. Root-caused via the identical bug already fixed
// for the Landing passcode fields (see DECISIONS.md); submit-time
// validation (validateMove) always does a full, authoritative re-check.
export function validateMoveAmountBlur() {
  const s = getState();
  const m = s.move;
  if (!m.amount) {
    setSilent((st) => ({ move: { ...st.move, errors: { ...st.move.errors, amount: null } } }));
    return;
  }
  const account = s.wallets.find((w) => w.id === m.fromAccountId);
  const avail = m.ccy === s.reportingCurrency ? totalUSDFor(account) : (account.balances[m.ccy] || 0);
  const amt = parseFloat(m.amount);
  let error = null;
  if (Number.isNaN(amt) || amt <= 0) error = 'Enter a valid amount';
  else if (amt > avail) error = 'Exceeds available balance';
  setSilent((st) => ({ move: { ...st.move, errors: { ...st.move.errors, amount: error } } }));
}

export function onMoveContinue() {
  const errors = validateMove();
  if (Object.keys(errors).length) { setState((s) => ({ move: { ...s.move, errors } })); return; }
  setState((s) => ({ move: { ...s.move, step: 'review' } }));
}
export function onMoveBack() {
  setState((s) => ({ move: { ...s.move, step: 'form' } }));
}

export async function onMoveConfirm() {
  // Guard against double-submit: a fast double-click before the first
  // re-render lands must not fire two signed transfers.
  if (getState().move.sending) return;
  setState((s) => ({ move: { ...s.move, sending: true, errors: { ...s.move.errors, submit: null } } }));

  const s = getState();
  const m = s.move;
  const amt = parseFloat(m.amount);
  const wallets = s.wallets.map((w) => ({ ...w, balances: { ...w.balances } }));
  const src = wallets.find((w) => w.id === m.fromAccountId);
  let deductCcy = m.ccy;
  let deductAmt = amt;
  let fxRate = null; // set only when the amount was entered in the reporting currency and converted to a stablecoin — the actual "two currencies" case feature-ui.md means by an FX transfer
  if (m.ccy === s.reportingCurrency) {
    deductCcy = Object.keys(src.balances)[0];
    fxRate = FX[deductCcy] || 1;
    deductAmt = amt / fxRate;
  }
  src.balances[deductCcy] = +(src.balances[deductCcy] - deductAmt).toFixed(2);

  let txId, hash;
  try {
    ({ txId, hash } = await walletCall('signTransfer', {
      fromAccountId: m.fromAccountId, toKey: m.toKey, amount: deductAmt, ccy: deductCcy
    }));
  } catch {
    setState((st) => ({ move: { ...st.move, sending: false, errors: { ...st.move.errors, submit: 'Transfer failed — try again' } } }));
    return;
  }

  const now = new Date();
  const dateStr = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const dateISO = now.toISOString().slice(0, 10);

  const newTxs = [];
  const [kind, refId] = m.toKey.split(':');
  const txType = fxRate ? 'fx' : 'out';
  let resultLabel = '';
  if (kind === 'account') {
    const dst = wallets.find((w) => w.id === refId);
    dst.balances[deductCcy] = +((dst.balances[deductCcy] || 0) + deductAmt).toFixed(2);
    newTxs.push({ id: txId + 'A', date: dateStr, dateISO, type: txType, accountId: src.id, desc: src.name + ' → ' + dst.name, wallet: src.name, ccy: deductCcy, amount: -deductAmt, hash, fxRate });
    newTxs.push({ id: txId + 'B', date: dateStr, dateISO, type: 'in', accountId: dst.id, desc: src.name + ' → ' + dst.name, wallet: dst.name, ccy: deductCcy, amount: deductAmt, hash, fxRate });
    resultLabel = dst.name;
  } else {
    const recipient = s.savedRecipients.find((r) => r.id === refId) || { name: 'Unknown' };
    newTxs.push({ id: txId, date: dateStr, dateISO, type: txType, accountId: src.id, desc: 'Transfer to ' + recipient.name, wallet: src.name, ccy: deductCcy, amount: -deductAmt, hash, fxRate });
    resultLabel = recipient.name;
  }
  haptic(25);
  setState((st) => ({
    wallets,
    transactions: [...newTxs, ...st.transactions],
    move: { ...st.move, step: 'success', sending: false, resultTx: { id: txId, hash, amount: amt, ccy: m.ccy, label: resultLabel } }
  }));
}

export function onMoveDone() {
  setState({
    move: { fromAccountId: 'ops', ccy: 'USDC', chain: NET.USDC, toKey: '', amount: '', memo: '', step: 'form', errors: {}, resultTx: null, sending: false },
    screen: 'home'
  });
}

/* ---- Add recipient --------------------------------------------------- */
export function openAddRecipient() {
  setState({ showAddRecipient: true, newRecipName: '', newRecipAddress: '', newRecipError: '', recipientEdit: null });
}
export function cancelAddRecipient() {
  setState({ showAddRecipient: false, recipientEdit: null });
}
export function validateRecipientAddressBlur() {
  const s = getState();
  const addr = (s.recipientEdit ? s.recipientEdit.address : s.newRecipAddress).trim();
  if (!addr) { setState({ newRecipError: '' }); return; }
  setState({ newRecipError: addr.length < 8 ? 'Enter a valid wallet address' : '' });
}
// Handles both create (recipientEdit === null) and edit (openEditRecipient
// having populated it) — same modal, same validation, so edits get the same
// address-format check a new recipient does.
export function saveRecipient() {
  const s = getState();
  const editing = s.recipientEdit;
  const name = (editing ? editing.name : s.newRecipName).trim();
  const address = (editing ? editing.address : s.newRecipAddress).trim();
  if (!name) { setState({ newRecipError: 'Enter a name' }); return; }
  if (address.length < 8) { setState({ newRecipError: 'Enter a valid wallet address' }); return; }

  if (editing) {
    const list = s.savedRecipients.map((r) => (r.id === editing.id ? { ...r, name, address } : r));
    setState({ savedRecipients: list, showAddRecipient: false, recipientEdit: null });
    persistRecipients(list);
    showToast('Recipient updated');
    return;
  }

  const id = 'r' + Date.now();
  const rec = { id, name, address };
  const list = [...s.savedRecipients, rec];
  setState((st) => ({
    savedRecipients: list,
    move: { ...st.move, toKey: 'recipient:' + id, errors: { ...st.move.errors, toKey: null } },
    showAddRecipient: false
  }));
  persistRecipients(list);
}

/* ---- Activity / txns -------------------------------------------------- */
export function setTxnFilter(f) { setState({ txnFilter: f }); }
export function setTxnAccountFilter(f) { setState({ txnAccountFilter: f }); }
export function setTxnTimeFilter(f) { setState({ txnTimeFilter: f }); }
export function setTxnSort(f) { setState({ txnSort: f }); }
// setState, not setSilent: the search term drives which rows are visible,
// so every keystroke must re-render. The input opts into app.js's
// focus/caret-preservation (data-preserve-focus) so that full re-render
// doesn't lose focus or dismiss the on-screen keyboard the way it would
// without it — see the comment on render() in app.js.
export function setTxnSearch(v) { setState({ txnSearch: v }); }
export function toggleExpand(id) {
  setState((s) => ({ expandedTxId: s.expandedTxId === id ? null : id, tagDraft: '' }));
}

function persistTxnTags(tags) {
  try { localStorage.setItem('tynti_txn_tags', JSON.stringify(tags)); } catch { /* storage unavailable */ }
}
export function setTagDraft(v) {
  setSilent({ tagDraft: v });
}
export function addTxnTag(txId) {
  const s = getState();
  const tag = s.tagDraft.trim();
  if (!tag) return;
  const existing = s.txnTags[txId] || [];
  if (existing.some((t) => t.toLowerCase() === tag.toLowerCase())) {
    setState({ tagDraft: '' });
    return;
  }
  const txnTags = { ...s.txnTags, [txId]: [...existing, tag] };
  persistTxnTags(txnTags);
  setState({ txnTags, tagDraft: '' });
}
export function removeTxnTag(txId, tag) {
  const s = getState();
  const txnTags = { ...s.txnTags, [txId]: (s.txnTags[txId] || []).filter((t) => t !== tag) };
  persistTxnTags(txnTags);
  setState({ txnTags });
}

// Both exports pull from filteredTransactions, not the raw transaction
// list — feature-ui.md asks for "the displayed activity rows", so what
// downloads must always match whatever filters/search/sort are currently
// applied on screen, not the account's full history.
export function exportCSV() {
  const rows = [['Date', 'Type', 'Description', 'Account', 'Currency', 'Amount', 'Tx Hash']];
  filteredTransactions(getState()).forEach((t) => rows.push([t.date, t.type, t.desc, t.wallet, t.ccy, t.amount, t.hash]));
  const csv = rows.map((r) => r.map((v) => '"' + String(v).replace(/"/g, '""') + '"').join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'tynti-transactions.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  showToast('CSV downloaded');
}
export function exportPDF() {
  const rows = filteredTransactions(getState()).map((t) => ({
    date: t.date,
    desc: t.desc,
    wallet: t.wallet,
    amount: (t.amount < 0 ? '-' : '+') + Math.abs(t.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + t.ccy,
    hash: t.hash
  }));
  const blob = buildStatementPdf({
    subtitle: 'Account Statement',
    generatedLine: 'Generated ' + new Date().toLocaleString('en-US'),
    rows
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'tynti-statement.pdf';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  showToast('PDF downloaded');
}

/* ---- Settings: preferences ---------------------------------------------- */
const SETTINGS_KEYS = ['testnetEnabled', 'defaultStablecoin', 'defaultChain'];
function persistSettings(s) {
  const patch = {};
  SETTINGS_KEYS.forEach((k) => { patch[k] = s[k]; });
  try { localStorage.setItem('tynti_settings', JSON.stringify(patch)); } catch { /* storage unavailable */ }
}
export function setTestnetEnabled(v) {
  setState({ testnetEnabled: v });
  persistSettings(getState());
}
export function setDefaultStablecoin(ccy) {
  setState({ defaultStablecoin: ccy, defaultChain: (CHAINS[ccy] || [])[0] || '' });
  persistSettings(getState());
}
export function setDefaultChain(chain) {
  setState({ defaultChain: chain });
  persistSettings(getState());
}

/* ---- Settings: profile details ------------------------------------------ */
export function startProfileEdit(profileId, field) {
  const p = getState().profiles.find((p) => p.id === profileId);
  if (!p) return;
  setState({ profileEdit: { profileId, field, draft: field === 'name' ? p.name : (p.email || ''), error: '' } });
}
export function setProfileEditDraft(v) {
  setSilent((s) => ({ profileEdit: { ...s.profileEdit, draft: v, error: '' } }));
}
export function cancelProfileEdit() {
  setState({ profileEdit: { profileId: null, field: null, draft: '', error: '' } });
}
export function confirmProfileEdit() {
  const s = getState();
  const { profileId, field, draft } = s.profileEdit;
  if (!profileId || !field) return;
  const value = draft.trim();
  if (field === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
    setState((st) => ({ profileEdit: { ...st.profileEdit, error: 'Enter a valid email address' } }));
    return;
  }
  if (field === 'name' && !value) {
    setState((st) => ({ profileEdit: { ...st.profileEdit, error: 'Name cannot be empty' } }));
    return;
  }
  const profiles = s.profiles.map((p) => (p.id === profileId ? { ...p, [field]: value } : p));
  persistProfiles(profiles);
  setState({ profiles, profileEdit: { profileId: null, field: null, draft: '', error: '' } });
}
export function setProfileAvatar(profileId, avatar) {
  const profiles = getState().profiles.map((p) => (p.id === profileId ? { ...p, avatar } : p));
  persistProfiles(profiles);
  setState({ profiles });
}

/* ---- Settings / private keys ------------------------------------------ */
export async function refreshKeys() {
  let keys;
  try {
    keys = await walletCall('listKeys');
  } catch {
    showToast("Couldn't load keys — try again");
    return;
  }
  setState((s) => ({
    privateKeys: keys.map((k) => {
      const existing = s.privateKeys.find((pk) => pk.id === k.id);
      return { id: k.id, label: k.label, revealed: existing ? existing.revealed : false, words: existing ? existing.words : null };
    })
  }));
}

// Called once at boot (main.js) — the check itself is async, so this
// can't just be computed inline in store.js's initial state. Device
// capability only; whether a given profile also has a passkey enrolled is
// `profile.vault.webauthn`, checked separately in the render layer.
export async function checkWebauthnAvailable() {
  const available = await platformAuthenticatorAvailable();
  setState({ webauthnAvailable: available });
}

export async function ensureDefaultKey() {
  try {
    const keys = await walletCall('listKeys');
    if (keys.length === 0) {
      await walletCall('newKey', { label: 'Default' });
    }
    setState({ workerConnected: true });
  } catch {
    setState({ workerConnected: false });
    return;
  }
  await refreshKeys();
}

// Hiding an already-revealed key needs no re-auth; revealing one does
// (see requestReveal/submitConfirmReveal below) — CLAUDE.md requires a
// confirmation/auth step before key material reaches the main thread.
export function togglePrivateKey(id) {
  const s = getState();
  const key = s.privateKeys.find((k) => k.id === id);
  if (!key) return;
  if (key.revealed) {
    setState((st) => ({ privateKeys: st.privateKeys.map((k) => (k.id === id ? { ...k, revealed: false, words: null } : k)) }));
    return;
  }
  requestReveal(id);
}

export function requestReveal(id) {
  setState({ showConfirmReveal: true, confirmRevealKeyId: id, confirmRevealCode: '', confirmRevealError: '' });
}
export function cancelConfirmReveal() {
  setState({ showConfirmReveal: false, confirmRevealKeyId: null, confirmRevealCode: '', confirmRevealError: '' });
}
export function setConfirmRevealCode(v) {
  setSilent({ confirmRevealCode: v.replace(/\D/g, '').slice(0, 6), confirmRevealError: '' });
}
// Re-auth via the same vault the app unlocks with (CLAUDE.md: reveal
// requires explicit confirmation) — a successful unwrap of the vault key
// IS the check; there's no separate verifier to keep in sync with it.
// Doesn't need the vault key itself beyond that proof, so it isn't stored
// anywhere — this re-check is deliberately independent of whatever vault
// key the current session already holds from unlocking.
async function revealAfterVaultCheck(vaultKeyOrNull) {
  if (!vaultKeyOrNull) {
    setState({ confirmRevealError: 'Incorrect passcode' });
    return;
  }
  const s = getState();
  const id = s.confirmRevealKeyId;
  let words;
  try {
    ({ words } = await walletCall('revealKey', { id }));
  } catch {
    setState({ showConfirmReveal: false, confirmRevealKeyId: null, confirmRevealCode: '', confirmRevealError: '' });
    showToast("Couldn't reveal key — try again");
    return;
  }
  setState((st) => ({
    privateKeys: st.privateKeys.map((k) => (k.id === id ? { ...k, revealed: true, words } : k)),
    showConfirmReveal: false, confirmRevealKeyId: null, confirmRevealCode: '', confirmRevealError: ''
  }));
}
export async function submitConfirmReveal() {
  const s = getState();
  const profile = s.profiles.find((p) => p.id === s.activeProfileId) || s.profiles[0];
  let vaultKey = null;
  if (profile) {
    try { vaultKey = await unwrapWithPin(s.confirmRevealCode, profile.vault.pin); } catch { /* wrong PIN */ }
  }
  await revealAfterVaultCheck(vaultKey);
}
export async function confirmRevealWithWebauthn() {
  const s = getState();
  const profile = s.profiles.find((p) => p.id === s.activeProfileId) || s.profiles[0];
  if (!profile || !profile.vault.webauthn) return;
  const vaultKey = await unwrapWithWebauthn(profile.vault.webauthn).catch(() => null);
  if (!vaultKey) { setState({ confirmRevealError: 'Biometric check failed — enter your passcode' }); return; }
  await revealAfterVaultCheck(vaultKey);
}

export function toggleRecoveryInfo() {
  setState((s) => ({ showRecoveryInfo: !s.showRecoveryInfo }));
}

export function openImportKey() {
  setState({ showImportKey: true, importText: '', importError: '' });
}
export function cancelImportKey() {
  setState({ showImportKey: false });
}
export function validateImportPhraseBlur() {
  const text = getState().importText.trim();
  if (!text) { setState({ importError: '' }); return; }
  const words = text.split(/\s+/).filter(Boolean);
  setState({ importError: words.length < 12 ? 'Enter a valid recovery phrase' : '' });
}
export async function saveImportKey() {
  const words = getState().importText.trim().split(/\s+/).filter(Boolean);
  if (words.length < 12) { setState({ importError: 'Enter a valid recovery phrase' }); return; }
  const n = getState().privateKeys.length + 1;
  const label = 'Imported' + (n > 1 ? ' ' + n : '');
  try {
    await walletCall('importKey', { words, label });
  } catch (err) {
    setState({ importError: err.message || 'Import failed' });
    return;
  }
  await refreshKeys();
  setState({ showImportKey: false });
  showToast('Key imported');
}

/* ---- Lock ------------------------------------------------------------ */
export function goLock() {
  haptic();
  clearSessionVault();
  // Clears sensitive in-memory state on lock, per CLAUDE.md. savedRecipients
  // is encrypted at rest, but the *decrypted* copy held in state while
  // unlocked is cleared here same as privateKeys' revealed words.
  setState((s) => ({
    screen: 'landing',
    landingFormOpen: false,
    landingAddingProfile: false,
    lockCode: '',
    lockError: '',
    savedRecipients: [],
    privateKeys: s.privateKeys.map((k) => ({ ...k, revealed: false, words: null }))
  }));
}
export function setLockCode(v) {
  // setSilent, not setState: a full re-render on every digit would tear
  // down and recreate the <input>, dismissing the on-screen keyboard
  // after each keystroke on mobile.
  setSilent({ lockCode: v.replace(/\D/g, '').slice(0, 6), lockError: '' });
}
export async function unlock() {
  if (getState().lockChecking) return;
  const s = getState();
  const profile = s.profiles.find((p) => p.id === s.activeProfileId) || s.profiles[0];
  if (!profile) { setState({ lockError: 'No account found on this device' }); return; }

  setState({ lockChecking: true, lockError: '' });
  let vaultKey = null;
  try {
    vaultKey = await unwrapWithPin(getState().lockCode, profile.vault.pin);
  } catch { /* wrong PIN — AES-GCM auth-tag failure, treated as incorrect below */ }

  if (vaultKey) {
    setSessionVault(profile.id, vaultKey);
    await loadRecipientsForSession();
    setState({ screen: 'home', lockCode: '', lockChecking: false });
  } else {
    setState({ lockError: 'Incorrect passcode', lockChecking: false });
  }
}

// Biometric unlock — a convenience layer over the same vault the PIN path
// unwraps (see vault.js); any failure here (cancelled prompt, no
// matching authenticator, PRF unavailable this time) falls back to the
// always-present PIN UI rather than surfacing an error of its own, since
// PIN entry is still right there on the same screen.
export async function unlockWithWebauthn() {
  if (getState().lockChecking) return;
  const s = getState();
  const profile = s.profiles.find((p) => p.id === s.activeProfileId) || s.profiles[0];
  if (!profile || !profile.vault.webauthn) return;

  setState({ lockChecking: true, lockError: '' });
  const vaultKey = await unwrapWithWebauthn(profile.vault.webauthn).catch(() => null);

  if (vaultKey) {
    setSessionVault(profile.id, vaultKey);
    await loadRecipientsForSession();
    setState({ screen: 'home', lockChecking: false });
  } else {
    setState({ lockChecking: false, lockError: 'Biometric unlock failed — enter your passcode' });
  }
}

/* ---- Landing / onboarding / profiles ---------------------------------- */
function persistProfiles(list) {
  try { localStorage.setItem('tynti_profiles', JSON.stringify(list)); } catch { /* storage unavailable */ }
}
function persistActiveProfile(id) {
  try { localStorage.setItem('tynti_active_profile', id); } catch { /* storage unavailable */ }
}

export function openLandingForm() {
  setState({
    landingFormOpen: true,
    newProfileName: '', newProfileEmail: '', newProfileAnonymous: false, newProfileNameBeforeAnon: '',
    newProfileCode: '', newProfileCodeConfirm: '', agreeTerms: false, agreeSelfCustody: false,
    newProfileErrors: {}
  });
}

export function setNewProfileName(v) {
  setSilent((s) => ({ newProfileName: v, newProfileErrors: { ...s.newProfileErrors, name: null } }));
}
export function setNewProfileEmail(v) {
  setSilent((s) => ({ newProfileEmail: v, newProfileErrors: { ...s.newProfileErrors, email: null } }));
}
// Anonymous forces Name to "Anon" and clears Email (both fields grey out via
// `disabled` in the render layer — no inline styles, CSP forbids those).
// The typed name is snapshotted so un-checking restores it instead of
// losing what the user already entered.
export function toggleNewProfileAnonymous() {
  setState((s) => {
    if (!s.newProfileAnonymous) {
      return {
        newProfileAnonymous: true,
        newProfileNameBeforeAnon: s.newProfileName,
        newProfileName: 'Anon',
        newProfileEmail: '',
        newProfileErrors: { ...s.newProfileErrors, name: null, email: null }
      };
    }
    return { newProfileAnonymous: false, newProfileName: s.newProfileNameBeforeAnon || '' };
  });
}
export function setNewProfileCode(v) {
  setSilent((s) => ({ newProfileCode: v.replace(/\D/g, '').slice(0, 6), newProfileErrors: { ...s.newProfileErrors, code: null } }));
}
export function setNewProfileCodeConfirm(v) {
  setSilent((s) => ({ newProfileCodeConfirm: v.replace(/\D/g, '').slice(0, 6), newProfileErrors: { ...s.newProfileErrors, code: null } }));
}
export function toggleAgreeTerms() {
  setState((s) => ({ agreeTerms: !s.agreeTerms, newProfileErrors: { ...s.newProfileErrors, agree: null } }));
}
export function toggleAgreeSelfCustody() {
  setState((s) => ({ agreeSelfCustody: !s.agreeSelfCustody, newProfileErrors: { ...s.newProfileErrors, agree: null } }));
}

// A pop-up (modal), not a full screen — feature-ui.md calls for
// it from several places (Landing's checkboxes, its FAQ, Status Panel)
// without navigating away from wherever it was opened.
export function openLicense() {
  setState({ showLicense: true });
}
export function closeLicense() {
  setState({ showLicense: false });
}

// Blur-time checks only verify *format*, matching the rest of the app's
// inline-validation convention (send/recipient/import-key blur handlers) —
// required-ness is a submit-time-only check so an untouched field doesn't
// show an error before the user has typed anything.
// setSilent, not setState: this form has two adjacent fields (code ->
// confirm) where blurring one to focus the next is the normal flow. A
// setState here fully clears+rebuilds the DOM (see app.js's render()) —
// doing that mid-transition races the browser's focus handoff into the
// next field and can silently drop the keystrokes typed there. setSilent
// still updates state immediately; the error just renders on whatever
// state change happens next (the next field's own edit, or submit).
export function validateNewProfileEmailBlur() {
  const s = getState();
  const email = s.newProfileEmail.trim();
  if (!email) { setSilent((st) => ({ newProfileErrors: { ...st.newProfileErrors, email: null } })); return; }
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  setSilent((st) => ({ newProfileErrors: { ...st.newProfileErrors, email: valid ? null : 'Enter a valid email address' } }));
}
export function validateNewProfileCodeBlur() {
  const s = getState();
  if (!s.newProfileCode) { setSilent((st) => ({ newProfileErrors: { ...st.newProfileErrors, code: null } })); return; }
  const valid = /^\d{6}$/.test(s.newProfileCode);
  setSilent((st) => ({ newProfileErrors: { ...st.newProfileErrors, code: valid ? null : 'Passcode must be 6 digits' } }));
}
export function validateNewProfileCodeConfirmBlur() {
  const s = getState();
  if (!s.newProfileCodeConfirm) { setSilent((st) => ({ newProfileErrors: { ...st.newProfileErrors, code: null } })); return; }
  const valid = s.newProfileCode === s.newProfileCodeConfirm;
  setSilent((st) => ({ newProfileErrors: { ...st.newProfileErrors, code: valid ? null : 'Passcodes do not match' } }));
}

function validateNewProfile(s) {
  const errors = {};
  if (!s.newProfileAnonymous && !s.newProfileName.trim()) errors.name = 'Enter your name or company name';
  if (s.newProfileEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.newProfileEmail.trim())) errors.email = 'Enter a valid email address';
  if (!/^\d{6}$/.test(s.newProfileCode)) errors.code = 'Enter a 6-digit passcode';
  else if (s.newProfileCode !== s.newProfileCodeConfirm) errors.code = 'Passcodes do not match';
  if (!s.agreeTerms || !s.agreeSelfCustody) errors.agree = 'Agree to both statements to continue';
  return errors;
}

export async function submitOpenAccount() {
  if (getState().newProfileSubmitting) return;
  const s = getState();
  const errors = validateNewProfile(s);
  if (Object.keys(errors).length) { setState({ newProfileErrors: errors }); return; }

  setState({ newProfileSubmitting: true });

  const profileName = s.newProfileAnonymous ? 'Anon' : s.newProfileName.trim();
  let vaultKey, pinWrap;
  try {
    vaultKey = await generateVaultKey();
    pinWrap = await createPinWrap(s.newProfileCode, vaultKey);
  } catch {
    setState({ newProfileSubmitting: false, newProfileErrors: { agree: "Couldn't set up your passcode — try again" } });
    return;
  }
  // Silent no-op (not an error) when WebAuthn/prf isn't available or the
  // user declines the platform prompt — the PIN wrap above is already a
  // fully secure, fully functional vault on its own (see vault.js).
  const webauthnWrap = await registerWebauthn(profileName, vaultKey).catch(() => null);

  const profile = {
    id: 'p' + Date.now(),
    name: profileName,
    email: s.newProfileAnonymous ? '' : s.newProfileEmail.trim(),
    anonymous: s.newProfileAnonymous,
    avatar: CONFIG.AVATARS[0], // circleUserRound — default; changeable afterward in Settings
    vault: { pin: pinWrap, webauthn: webauthnWrap },
    createdAt: new Date().toISOString()
  };
  const profiles = [...getState().profiles, profile];
  persistProfiles(profiles);
  persistActiveProfile(profile.id);
  setSessionVault(profile.id, vaultKey);
  await loadRecipientsForSession(); // always [] for a brand-new profile, but keeps state consistent with the unlock paths
  setState({
    profiles, activeProfileId: profile.id,
    screen: 'home', landingFormOpen: false, landingAddingProfile: false,
    newProfileSubmitting: false,
    newProfileName: '', newProfileEmail: '', newProfileAnonymous: false, newProfileNameBeforeAnon: '',
    newProfileCode: '', newProfileCodeConfirm: '', agreeTerms: false, agreeSelfCustody: false, newProfileErrors: {}
  });
  showToast(webauthnWrap ? 'Account opened — biometric unlock enabled' : 'Account opened');
}
